import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = 'https://csksportswear.in/backend/api';

function Reports() {
  const [bills, setBills] = useState([]);
  const [billItems, setBillItems] = useState({});
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('sales');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('csk_user') || '{}');

  useEffect(() => {
    const tok = localStorage.getItem('csk_token');
    if (!tok) { navigate('/login'); return; }
    fetchData(tok);
  }, []);

  const fetchData = async (tok) => {
    try {
      const [billsRes, productsRes] = await Promise.all([
        axios.get(`${API}/bills.php?action=list`, {
          headers: { Authorization: `Bearer ${tok}` }
        }),
        axios.get(`${API}/inventory.php?action=list`, {
          headers: { Authorization: `Bearer ${tok}` }
        }),
      ]);

      if (billsRes.data.success) {
        const billsData = billsRes.data.data;
        setBills(billsData);

        // Fetch items for each bill
        const itemsMap = {};
        await Promise.all(
          billsData.map(async (bill) => {
            try {
              const res = await axios.get(`${API}/bills.php?action=get&id=${bill.id}`, {
                headers: { Authorization: `Bearer ${tok}` }
              });
              if (res.data.success && res.data.data.items) {
                itemsMap[bill.id] = res.data.data.items;
              } else {
                itemsMap[bill.id] = [];
              }
            } catch (e) {
              itemsMap[bill.id] = [];
            }
          })
        );
        setBillItems(itemsMap);
      }

      if (productsRes.data.success) {
        setProducts(productsRes.data.data);
      }
    } catch (err) {
      console.error('fetchData error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProductNames = (billId) => {
    const items = billItems[billId] || [];
    if (items.length === 0) return '-';
    return items.map(i => `${i.product_name} (x${i.quantity})`).join(', ');
  };

  const formatDateTime = (dateStr) => new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });

  const filtered = bills.filter(b => {
    if (!dateFrom && !dateTo) return true;
    const billDate = new Date(b.created_at);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;
    if (from && billDate < from) return false;
    if (to && billDate > to) return false;
    return true;
  });

  const totalRevenue = filtered.reduce((sum, b) => sum + parseFloat(b.total || 0), 0);
  const totalGST = filtered.reduce((sum, b) => sum + parseFloat(b.gst_amount || 0), 0);
  const totalSubtotal = filtered.reduce((sum, b) => sum + parseFloat(b.subtotal || 0), 0);

  const generateSalesPDF = () => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    doc.setFillColor(26, 26, 46);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 203, 5);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CSK SPORTSWEAR', pageWidth / 2, 15, { align: 'center' });
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text('SALES REPORT', pageWidth / 2, 24, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Period: ${dateFrom || 'All'} to ${dateTo || 'All'} | Generated: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, 31, { align: 'center' });

    const summaryData = [
      ['Total Bills', filtered.length],
      ['Total Revenue', 'Rs.' + totalRevenue.toFixed(2)],
      ['Total GST', 'Rs.' + totalGST.toFixed(2)],
      ['Avg Bill', 'Rs.' + (filtered.length > 0 ? (totalRevenue / filtered.length).toFixed(2) : '0.00')],
    ];
    let xPos = 14;
    summaryData.forEach(([label, value]) => {
      doc.setFillColor(240, 242, 245);
      doc.roundedRect(xPos, 40, 60, 18, 2, 2, 'F');
      doc.setFontSize(7); doc.setTextColor(100, 116, 139); doc.setFont('helvetica', 'normal');
      doc.text(label, xPos + 30, 47, { align: 'center' });
      doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold');
      doc.text(String(value), xPos + 30, 54, { align: 'center' });
      xPos += 65;
    });

    autoTable(doc, {
      startY: 65,
      head: [['Bill No', 'Date & Time', 'Customer', 'Mobile', 'Products', 'Subtotal', 'GST', 'Total', 'Payment']],
      body: filtered.map(b => [
        b.bill_number,
        formatDateTime(b.created_at),
        b.customer_name || 'Customer',
        b.customer_mobile || '-',
        getProductNames(b.id),
        'Rs.' + parseFloat(b.subtotal || 0).toFixed(2),
        'Rs.' + parseFloat(b.gst_amount || 0).toFixed(2),
        'Rs.' + parseFloat(b.total || 0).toFixed(2),
        (b.payment_method || '').toUpperCase(),
      ]),
      foot: [['', '', '', '', 'TOTAL',
        'Rs.' + totalSubtotal.toFixed(2),
        'Rs.' + totalGST.toFixed(2),
        'Rs.' + totalRevenue.toFixed(2), '']],
      headStyles: { fillColor: [26, 26, 46], textColor: [255, 203, 5], fontStyle: 'bold', fontSize: 8 },
      footStyles: { fillColor: [240, 242, 245], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 7 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { cellPadding: 3 },
      columnStyles: { 4: { cellWidth: 60 } },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString('en-IN')} | Powered by Veltrix Technologies | Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.height - 8, { align: 'center' });
    }
    doc.save(`CSK_Sales_Report_${dateFrom || 'All'}_to_${dateTo || 'All'}.pdf`);
  };

  const generateInventoryPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    doc.setFillColor(26, 26, 46);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 203, 5);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CSK SPORTSWEAR', pageWidth / 2, 15, { align: 'center' });
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text('INVENTORY REPORT', pageWidth / 2, 24, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, 31, { align: 'center' });

    const inStock = products.filter(p => p.stock_status === 'in_stock').length;
    const lowStock = products.filter(p => p.stock_status === 'low_stock').length;
    const outStock = products.filter(p => p.stock_status === 'out_of_stock').length;
    const totalValue = products.reduce((sum, p) => sum + (parseFloat(p.stock || 0) * parseFloat(p.cost_price || 0)), 0);

    const summaryData = [['Total Products', products.length], ['In Stock', inStock], ['Low Stock', lowStock], ['Out of Stock', outStock]];
    let xPos = 14;
    summaryData.forEach(([label, value]) => {
      doc.setFillColor(240, 242, 245);
      doc.roundedRect(xPos, 40, 42, 18, 2, 2, 'F');
      doc.setFontSize(7); doc.setTextColor(100, 116, 139); doc.setFont('helvetica', 'normal');
      doc.text(label, xPos + 21, 47, { align: 'center' });
      doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold');
      doc.text(String(value), xPos + 21, 54, { align: 'center' });
      xPos += 46;
    });

    autoTable(doc, {
      startY: 65,
      head: [['#', 'Product Name', 'Category', 'SKU', 'Price', 'Cost', 'Stock', 'Min Stock', 'Status']],
      body: products.map((p, i) => [
        i + 1, p.name, p.category_name || '-', p.sku || '-',
        'Rs.' + parseFloat(p.price || 0).toFixed(2),
        'Rs.' + parseFloat(p.cost_price || 0).toFixed(2),
        p.stock, p.min_stock,
        p.stock_status === 'in_stock' ? 'In Stock' : p.stock_status === 'low_stock' ? 'Low Stock' : 'Out of Stock'
      ]),
      headStyles: { fillColor: [26, 26, 46], textColor: [255, 203, 5], fontStyle: 'bold', fontSize: 8 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      styles: { cellPadding: 3 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 8) {
          const val = data.cell.raw;
          if (val === 'Out of Stock') data.cell.styles.textColor = [220, 38, 38];
          else if (val === 'Low Stock') data.cell.styles.textColor = [245, 158, 11];
          else data.cell.styles.textColor = [22, 163, 74];
        }
      },
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFillColor(26, 26, 46);
    doc.roundedRect(14, finalY, 80, 14, 2, 2, 'F');
    doc.setTextColor(255, 203, 5); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text(`Total Stock Value: Rs.${totalValue.toFixed(2)}`, 54, finalY + 9, { align: 'center' });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleString('en-IN')} | Powered by Veltrix Technologies | Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.height - 8, { align: 'center' });
    }
    doc.save(`CSK_Inventory_Report_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
  };

  const menuItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '🏠' },
    { path: '/admin/inventory', label: 'Inventory', icon: '📦' },
    ...(user.role === 'admin' ? [{ path: '/admin/pos', label: 'POS Billing', icon: '🧾' }] : []),
    { path: '/admin/reports', label: 'Reports', icon: '📊' },
    { path: '/admin/gallery', label: 'Gallery', icon: '🖼️' },
    { path: '/admin/enquiries', label: 'Enquiries', icon: '📩' },
    ...(user.role === 'admin' ? [
      { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
      { path: '/admin/users', label: 'Users', icon: '👥' },
    ] : []),
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#0A0A0A', fontFamily: 'Inter, sans-serif' }}>
      <style>{`
        .rep-sidebar { width: 200px; background: #000; border-right: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 100; transition: transform 0.3s ease; }
        .rep-main { margin-left: 200px; flex: 1; padding: 32px; }
        .rep-mobile-hdr { display: none; background: #000; padding: 12px 16px; position: fixed; top: 0; left: 0; right: 0; z-index: 99; border-bottom: 1px solid rgba(255,255,255,0.08); align-items: center; justify-content: space-between; }
        .rep-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 99; }
        @media (max-width: 768px) {
          .rep-sidebar { transform: translateX(-100%); }
          .rep-sidebar.open { transform: translateX(0); }
          .rep-main { margin-left: 0; padding: 72px 12px 16px; }
          .rep-mobile-hdr { display: flex; }
          .rep-overlay.show { display: block; }
          .rep-cards { grid-template-columns: repeat(2, 1fr) !important; }
          .rep-hdr { flex-direction: column !important; gap: 12px !important; }
        }
        .rep-nav-link { display: flex; align-items: center; gap: 10px; padding: 12px 16px; color: #475569; font-size: 13px; font-weight: 500; text-decoration: none; border-left: 3px solid transparent; transition: all 0.2s; }
        .rep-nav-link:hover { color: white; background: rgba(255,255,255,0.05); }
        .rep-nav-link.active { color: white; background: rgba(0,102,255,0.1); border-left-color: #0066FF; }
      `}</style>

      {/* Mobile Header */}
      <div className="rep-mobile-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
          <span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 800, color: 'white' }}>CSK</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', fontSize: '22px', cursor: 'pointer' }}>☰</button>
      </div>

      <div className={`rep-overlay ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar */}
      <div className={`rep-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#DC2626', fontSize: '20px' }}>★</span>
            <div>
              <div style={{ fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 800, letterSpacing: '2px', color: 'white' }}>CSK</div>
              <div style={{ fontSize: '8px', color: '#475569', letterSpacing: '1px' }}>ADMIN PANEL</div>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'white' }}>{user.name}</div>
          <div style={{ fontSize: '11px', color: '#0066FF', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.role}</div>
        </div>
        <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
          {menuItems.map(item => (
            <Link key={item.path} to={item.path}
              className={`rep-nav-link ${window.location.pathname === item.path ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}>
              <span>{item.icon}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button onClick={() => { localStorage.clear(); navigate('/login'); }} style={{ width: '100%', padding: '8px', background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#DC2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>🚪 Logout</button>
        </div>
      </div>

      {/* Main */}
      <div className="rep-main">
        <div className="rep-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontFamily: 'Montserrat', fontSize: '28px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>Reports</h1>
            <p style={{ color: '#475569', fontSize: '14px' }}>Sales and inventory reports</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={generateSalesPDF} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #0066FF, #0052CC)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>📄 Sales PDF</button>
            <button onClick={generateInventoryPDF} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #16A34A, #15803D)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>📦 Inventory PDF</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
          {[{ id: 'sales', label: '📄 Sales Report' }, { id: 'inventory', label: '📦 Inventory Report' }].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', border: 'none', borderRadius: '8px', background: activeTab === tab.id ? '#0066FF' : 'rgba(255,255,255,0.05)', color: activeTab === tab.id ? 'white' : '#475569', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Date Filter */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px', display: 'flex', gap: '16px', alignItems: 'flex-end', marginBottom: '24px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>From Date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: '100%', padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ flex: 1, minWidth: '140px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: '#475569', marginBottom: '6px' }}>To Date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: '100%', padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '13px', cursor: 'pointer' }}>Clear</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: '#475569', padding: '60px' }}>Loading...</div>
        ) : (
          <>
            {activeTab === 'sales' && (
              <>
                {/* Summary Cards */}
                <div className="rep-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  {[
                    { label: 'Total Bills', value: filtered.length, icon: '🧾', color: '#0066FF' },
                    { label: 'Total Revenue', value: '₹' + totalRevenue.toFixed(2), icon: '💰', color: '#16A34A' },
                    { label: 'Total GST', value: '₹' + totalGST.toFixed(2), icon: '📋', color: '#F59E0B' },
                    { label: 'Avg Bill', value: filtered.length > 0 ? '₹' + (totalRevenue / filtered.length).toFixed(2) : '₹0', icon: '📈', color: '#8B5CF6' },
                  ].map((card, i) => (
                    <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <div style={{ color: '#475569', fontSize: '11px', marginBottom: '6px' }}>{card.label}</div>
                          <div style={{ fontSize: '18px', fontFamily: 'Montserrat', fontWeight: 800, color: card.color }}>{card.value}</div>
                        </div>
                        <div style={{ fontSize: '24px' }}>{card.icon}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bills Table */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between' }}>
                    <h3 style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700, color: 'white' }}>Bill History</h3>
                    <span style={{ color: '#475569', fontSize: '13px' }}>{filtered.length} bills</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                          {['Bill No', 'Date & Time', 'Customer', 'Mobile', 'Products', 'Subtotal', 'GST', 'Total', 'Payment'].map(h => (
                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.length === 0 ? (
                          <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#475569' }}>No bills found</td></tr>
                        ) : filtered.map(bill => (
                          <tr key={bill.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0066FF', fontWeight: 600, whiteSpace: 'nowrap' }}>{bill.bill_number}</td>
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94A3B8', whiteSpace: 'nowrap' }}>{formatDateTime(bill.created_at)}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: 'white' }}>{bill.customer_name || '-'}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94A3B8' }}>{bill.customer_mobile || '-'}</td>
                            <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94A3B8', maxWidth: '200px' }}>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {getProductNames(bill.id)}
                              </div>
                            </td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: 'white' }}>₹{parseFloat(bill.subtotal || 0).toFixed(2)}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', color: '#F59E0B' }}>₹{parseFloat(bill.gst_amount || 0).toFixed(2)}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, color: '#16A34A' }}>₹{parseFloat(bill.total || 0).toFixed(2)}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ padding: '3px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', background: bill.payment_method === 'cash' ? 'rgba(22,163,74,0.1)' : bill.payment_method === 'upi' ? 'rgba(0,102,255,0.1)' : 'rgba(139,92,246,0.1)', color: bill.payment_method === 'cash' ? '#16A34A' : bill.payment_method === 'upi' ? '#0066FF' : '#8B5CF6' }}>
                                {bill.payment_method}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {filtered.length > 0 && (
                        <tfoot>
                          <tr style={{ borderTop: '2px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}>
                            <td colSpan={5} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, color: 'white' }}>TOTAL</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, color: 'white' }}>₹{totalSubtotal.toFixed(2)}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, color: '#F59E0B' }}>₹{totalGST.toFixed(2)}</td>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 700, color: '#16A34A' }}>₹{totalRevenue.toFixed(2)}</td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'inventory' && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between' }}>
                  <h3 style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700, color: 'white' }}>Inventory Status</h3>
                  <span style={{ color: '#475569', fontSize: '13px' }}>{products.length} products</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '650px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        {['#', 'Product', 'Category', 'SKU', 'Price', 'Cost', 'Stock', 'Min', 'Status'].map(h => (
                          <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '1px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p, i) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#475569' }}>{i + 1}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: 'white' }}>{p.name}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94A3B8' }}>{p.category_name || '-'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#94A3B8' }}>{p.sku || '-'}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#0066FF', fontWeight: 700 }}>₹{parseFloat(p.price || 0).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: 'white' }}>₹{parseFloat(p.cost_price || 0).toFixed(2)}</td>
                          <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: 800, color: 'white' }}>{p.stock}</td>
                          <td style={{ padding: '12px 16px', fontSize: '13px', color: '#475569' }}>{p.min_stock}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: '50px', fontSize: '11px', fontWeight: 600, background: p.stock_status === 'in_stock' ? 'rgba(22,163,74,0.1)' : p.stock_status === 'low_stock' ? 'rgba(245,158,11,0.1)' : 'rgba(220,38,38,0.1)', color: p.stock_status === 'in_stock' ? '#16A34A' : p.stock_status === 'low_stock' ? '#F59E0B' : '#DC2626' }}>
                              {p.stock_status === 'in_stock' ? 'In Stock' : p.stock_status === 'low_stock' ? 'Low Stock' : 'Out of Stock'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Reports;
